import React, {useState, useEffect} from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Grid,
	Typography,
	Box,
	CircularProgress
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Select from 'react-select';
import API from '../../../../network/dashboardAPI';

const useStyles = makeStyles((theme) => ({
	formControl: {
		width: '100%',
		marginBottom: theme.spacing(3),
	},
	label: {
		marginBottom: theme.spacing(1),
		fontWeight: 500,
		color: theme.palette.text.secondary
	},
	selectContainer: {
		marginBottom: theme.spacing(3),
	}
}));

// Custom styles for react-select
const customSelectStyles = {
	control: (provided, state) => ({
		...provided,
		minHeight: 48,
		borderColor: state.isFocused ? '#9cc54b' : '#ccc',
		boxShadow: state.isFocused ? '0 0 0 1px #9cc54b' : 'none',
		'&:hover': {
			borderColor: '#9cc54b'
		}
	}),
	option: (provided, state) => ({
		...provided,
		backgroundColor: state.isSelected ? '#9cc54b' : state.isFocused ? '#e8f5e9' : 'white',
		color: state.isSelected ? 'white' : 'black',
		padding: '10px 12px',
		display: 'flex',
		alignItems: 'center',
		gap: '10px',
		'&:active': {
			backgroundColor: '#9cc54b'
		}
	}),
	singleValue: (provided) => ({
		...provided,
		display: 'flex',
		alignItems: 'center',
		gap: '10px'
	}),
	menu: (provided) => ({
		...provided,
		zIndex: 9999
	}),
	menuPortal: (provided) => ({
		...provided,
		zIndex: 9999
	})
};

const AssignDriverDialog = ({open, onClose, order, onAssign, colors}) => {
	const classes = useStyles();
	const [drivers, setDrivers] = useState([]);
	const [loading, setLoading] = useState(false);
	const [primaryDriverId, setPrimaryDriverId] = useState(null);
	const [secondaryDriverId, setSecondaryDriverId] = useState(null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (open) {
			loadDrivers();
			// Initialize selections based on order data
			// Note: order.driver is the object, order.driver_id is the ID
			setPrimaryDriverId(order.driver_id || null);

			// Note: order.secondaryDriver might be the object.
			// The database field is 'secondary_driver', not 'secondary_driver_id'
			setSecondaryDriverId(order.secondary_driver || order.secondaryDriver?.id || null);
		}
	}, [open, order]);

	const loadDrivers = async () => {
		setLoading(true);
		try {
			// Include store users who can act as drivers
			const res = await API.getDriverList({
				next: 0,
				limit: 1000,
				showType: false,
				includeStoreDrivers: true
			}).then(r => r.json());
			if (res.success && res.data && res.data.list) {
				setDrivers(res.data.list);
			}
		} catch (error) {
			console.error("Failed to load drivers", error);
		} finally {
			setLoading(false);
		}
	};

	const handleSave = async () => {
		setSaving(true);
		await onAssign(primaryDriverId, secondaryDriverId);
		setSaving(false);
		onClose();
	};

	const handlePrimaryChange = (selectedOption) => {
		const newValue = selectedOption ? selectedOption.value : null;
		setPrimaryDriverId(newValue);
		// Prevent same driver in both slots
		if (newValue === secondaryDriverId && newValue !== null) {
			setSecondaryDriverId(null);
		}
	};

	const handleSecondaryChange = (selectedOption) => {
		const newValue = selectedOption ? selectedOption.value : null;
		setSecondaryDriverId(newValue);
		if (newValue === primaryDriverId && newValue !== null) {
			setPrimaryDriverId(null);
		}
	};

	// Convert drivers to react-select options format
	const driverOptions = drivers.map(driver => ({
		value: driver.id,
		label: `${driver.first_name} ${driver.last_name}`,
		phone: driver.phone,
		profile_pic: driver.profile_pic,
		driver: driver
	}));

	// Get selected driver option
	const getSelectedOption = (driverId) => {
		if (!driverId) return null;
		return driverOptions.find(option => option.value === driverId) || null;
	};

	// Filter options for secondary driver (exclude primary)
	const secondaryDriverOptions = driverOptions.filter(option => option.value !== primaryDriverId);

	// Custom option component to show avatar and phone
	const formatOptionLabel = ({label, phone, profile_pic}) => (
		<div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
			<div style={{
				width: 30,
				height: 30,
				borderRadius: '50%',
				backgroundColor: profile_pic ? 'transparent' : '#e0e0e0',
				backgroundImage: profile_pic ? `url(${profile_pic})` : 'none',
				backgroundSize: 'cover',
				backgroundPosition: 'center',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				fontSize: '12px',
				color: '#666'
			}}>
				{!profile_pic && label.charAt(0).toUpperCase()}
			</div>
			<div>
				<div style={{fontWeight: 500}}>{label}</div>
				{phone && <div style={{fontSize: '12px', color: '#888'}}>{phone}</div>}
			</div>
		</div>
	);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="sm"
			aria-labelledby="assign-driver-dialog-title"
		>
			<DialogTitle id="assign-driver-dialog-title">
				Assign Drivers
				<Typography variant="body2" color="textSecondary">
					Order ID: {order?.order_id}
				</Typography>
			</DialogTitle>
			<DialogContent dividers>
				{loading ? (
					<Box display="flex" justifyContent="center" p={3}>
						<CircularProgress/>
					</Box>
				) : (
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<div className={classes.selectContainer}>
								<Typography className={classes.label}>Primary Driver</Typography>
								<Select
									value={getSelectedOption(primaryDriverId)}
									onChange={handlePrimaryChange}
									options={driverOptions}
									isClearable
									isSearchable
									placeholder="Search and select primary driver..."
									styles={customSelectStyles}
									formatOptionLabel={formatOptionLabel}
									menuPortalTarget={document.body}
									noOptionsMessage={() => "No drivers found"}
								/>
							</div>
						</Grid>

						<Grid item xs={12}>
							<div className={classes.selectContainer}>
								<Typography className={classes.label}>Secondary Driver</Typography>
								<Select
									value={getSelectedOption(secondaryDriverId)}
									onChange={handleSecondaryChange}
									options={secondaryDriverOptions}
									isClearable
									isSearchable
									isDisabled={!primaryDriverId}
									placeholder={primaryDriverId ? "Search and select secondary driver..." : "Select primary driver first"}
									styles={customSelectStyles}
									formatOptionLabel={formatOptionLabel}
									menuPortalTarget={document.body}
									noOptionsMessage={() => "No drivers found"}
								/>
							</div>
						</Grid>
					</Grid>
				)}
			</DialogContent>
			<DialogActions style={{padding: '16px 24px'}}>
				<Button onClick={onClose} disabled={saving} variant="text">
					Cancel
				</Button>
				<Button
					onClick={handleSave}
					color="primary"
					variant="contained"
					disabled={saving}
					style={{backgroundColor: colors?.primary, color: '#fff'}}
				>
					{saving ? <CircularProgress size={24} color="inherit"/> : 'Save Assignments'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AssignDriverDialog;
